/** @file missingSheetsOf: the sheet names a new drawable uses that the puppet lacks, with their input paths. */
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';

const sheetName = checkString(NAME_PATTERN);
const facetSheets = (facets: { sheet: string }[] | undefined): [string, string][] =>
  (facets ?? []).map((e, i) => [e.sheet, `/input/drawable/facets/${i}/sheet`]);

/** Every sheet a drawable names, with its input path, per kind. */
const SHEETS_OF: { [K in Drawable['kind']]: (d: Extract<Drawable, { kind: K }>) => [string, string][] } = {
  impostor: (d) => facetSheets(d.facets),
  effect: (d) => [[d.frames.sheet, '/input/drawable/frames/sheet'], ...facetSheets(d.facets)],
  mesh: () => [],
  decal: () => [],
};

/** Unknown sheet names in first-use order, each once; a name that is not a sheet name is VALIDATION at its path. */
export function missingSheetsOf(puppet: Puppet, drawable: Drawable): string[] {
  const named = (SHEETS_OF[drawable.kind] as (d: Drawable) => [string, string][])(drawable);
  const out: string[] = [];
  for (const [name, path] of named) {
    if (Object.hasOwn(puppet.sheets, name) || out.includes(name)) continue;
    sheetName(name, path);
    out.push(name);
  }
  return out;
}
